import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector, shallowEqual } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRights } from 'providers/RightsConsumer';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import memoize from 'lodash.memoize';
import { Grid, Grow, Button, useMediaQuery, useTheme } from '@mui/material';
import { Build, LibraryAdd, List, Remove, History, FileCopy } from '@mui/icons-material';
import OfferAssetPropertiesComponent from './OfferAssetPropertiesComponent';
import OfferAssetEquipmentsComponent from './OfferAssetEquipmentsComponent';
import OfferAssetFinancialComponent from './OfferAssetFinancialComponent';
import OfferAssetSecondHandComponent from './OfferAssetSecondHandComponent';
import OfferAssetChassisNumberComponent from './OfferAssetChassisNumberComponent';
import OfferAssetChassisNumberPopupContainer from './OfferAssetChassisNumberPopupContainer';
import AssetOrderContainer from 'features/asset/order/components/AssetOrderContainer';
import OfferAssetFirstOwnerComponent from './OfferAssetFirstOwnerComponent';
import AssetSearchComponent from 'features/asset/hierarchy/components/AssetSearchComponent';
import OfferAssetInformationsContainer from './OfferAssetInformationsContainer';
import OfferAssetPartnerComponent from './OfferAssetPartnerComponent';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { RightsContext } from 'providers/RightsContext';
import debounce from 'debounce-promise';
import OfferAssetRequiredPropertiesComponent from './OfferAssetRequiredPropertiesComponent';
import { mandatoryFinancedAssetFields, mandatoryDealerAssetFields, mandatorySupplierAssetFields, supplierType } from '../constants';
import { COMBO, SEARCH, SEARCH_BIT, SELECT, TP_DATE, TP_NUM, TP_TEXT } from 'constants/inputTypes';
import { CollapseCard, IconButton } from '@totalsoft/rocket-ui';
import { OfferAssetImage, OfferAssetOptionsWrapper, StickyGrid, StickyGridWrapper } from './OfferAssetStyles';
import assetHierarchyFunctions from 'features/asset/hierarchy/assetHierarchyFunctions';
import { getDateValue } from 'utils/functions';
import OfferAssetWorkingLocationComponent from './OfferAssetWorkingLocationComponent';
import { useWorkLocationMandatory } from '../hooks/useUserWorkLocations';

const addPartnerPrefix = memoize(addPropertyPrefix('partner'));
const addPropertiesPrefix = memoize(addPropertyPrefix('properties'));

const OfferAssetComponentAlpha = React.memo(
  ({
    t,
    loadDealers,
    loadSuppliers,
    loadPartnerContactInfo,
    currencies,
    loadPropertyDetails,
    saving,
    duplicating,
    onPropertyChange,
    onSave,
    onDuplicate,
    rights,
    assetImage,
    isDirty,
    asset,
    hierarchy,
    isSearchingRestrictedDealer,
    handleChassisNumberSave,
    updatingChassisNumber,
    handleCancelChassisNumberSave,
    localChassisNumber,
    localSupplier,
    updateAssetChassisNo,
    financedAssetWrite,
    openOptions,
    handleOpenOptions,
    handleCloseOptions,
    openConfigurator,
    openPropertiesHistory,
    maxMonths,
    selfPartnerName,
    enableConfiguratorButton,
    editRight,
    onAssetDataChange,
    splitAsset,
    processing,
    isFacilityFrame,
    hasWorkingHours,
    assetPropertiesAreLoaded
  }) => {
    const { enableAssetConfigurator } = useContext(ApplicationFeaturesContext);

    const theme = useTheme();
    const underLg = useMediaQuery(theme.breakpoints.down('lg'));

    const document = asset.document;
    const validationInfo = asset.validationInfo;
    const workLocationIsMandatory = useWorkLocationMandatory(document);
    const configurationButtonVisibleRule = enableAssetConfigurator && enableConfiguratorButton;
    const propertiesHistoryButtonVisibleRule = document.propertiesHistoryEnabled;
    const propertiesDuplicateVisibleRule = document.propertiesDuplicateEnabled;
    const assetChassisNumberButtonVisibleRule = !financedAssetWrite && updateAssetChassisNo;
    const secondHandProperty = {
      value: document.km,
      label: t('OfferEdit.Asset.Info.AcquisitionKm'),
      error: validationInfo.km,
      mandatory: Boolean(validationInfo.km) || (document.isSecondHand && !isFacilityFrame && !hasWorkingHours && !document.km),
      disabled: document.category === 'New',
      visible: true,
      onChange: onPropertyChange('km')
    };
    const chassisNumberDisabled = !!document.quantity && document.quantity !== 1;
    const chassisNumberTooltip = chassisNumberDisabled ? t('OfferEdit.Asset.Info.ChassisNumberRequirementInfo') : '';
    const quantityDisabled = document.isChassisNumberAvailable;
    const quantityTooltip = quantityDisabled ? t('OfferEdit.Asset.Financial.QuantityRequirementInfo') : '';
    const actionsVisibleRules =
      configurationButtonVisibleRule ||
      assetChassisNumberButtonVisibleRule ||
      propertiesHistoryButtonVisibleRule ||
      (!document.withIndividualAssetData && document.quantity > 1);
    const [expandedCards, setExpandedCards] = useState({
      dealerCardExpanded: false,
      assetPropertiesCardExpanded: false
    });

    const actionRulesMap = new Map([
      ['offerAssetCard_assetActions_equipmentsConfigurator', configurationButtonVisibleRule],
      ['offerAssetCard_assetActions_propertiesHistory', propertiesHistoryButtonVisibleRule],
      ['OfferAssetChassisNumberPopupContainer', assetChassisNumberButtonVisibleRule],
      ['offerAssetCard_assetActions_splitAsset', !document.withIndividualAssetData && document.documentDetailId && document.quantity > 1]
    ]);

    const checkIfOnlyOneActionRuleIsVisible = useCallback(() => {
      let trueConditions = 0;
      if (configurationButtonVisibleRule) trueConditions++;
      if (assetChassisNumberButtonVisibleRule) trueConditions++;
      if (propertiesHistoryButtonVisibleRule) trueConditions++;
      if (!document.withIndividualAssetData && document.documentDetailId && document.quantity > 1) trueConditions++;
      return trueConditions === 1;
    }, [
      assetChassisNumberButtonVisibleRule,
      configurationButtonVisibleRule,
      document.documentDetailId,
      document.quantity,
      document.withIndividualAssetData,
      propertiesHistoryButtonVisibleRule
    ]);
    const onlyOneActionRuleIsVisible = checkIfOnlyOneActionRuleIsVisible();
    const { financedAssetDealerRead, financedAssetDealerWrite } = useContext(RightsContext);

    const getItemHierarchy = useCallback(() => {
      if (!hierarchy) return null;

      const { getHierarchyItem } = assetHierarchyFunctions(asset.document.hierarchy, hierarchy);
      return getHierarchyItem();
    }, [asset.document.hierarchy, hierarchy]);

    const mandatoryPropertiesConfigured = useSelector(
      state =>
        state.assetData.mandatoryPropertiesConfigured[
          `${document.itemId ?? ''}_${document.assetTypeId ?? ''}_${document.category ?? ''}_${document.classification ?? ''}`
        ]?.properties || [],
      shallowEqual
    );

    const getMandatoryFields = useMemo(() => {
      const emptyRequiredProps =
        (!isFacilityFrame &&
          document.properties?.filter(
            prop => !prop.value && prop.value !== 0 && (prop.mandatory || mandatoryPropertiesConfigured.includes(prop.propertyCode))
          )) ||
        [];

      mandatoryFinancedAssetFields.forEach(field => {
        if (!document[field.key]) emptyRequiredProps.push({ name: t(field.value) });
      });

      if (!document.category) emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.Category') });

      const hirarchyItem = getItemHierarchy();
      if (!hirarchyItem?.id) emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.Asset') });

      if (document.isSecondHand && !isFacilityFrame && !hasWorkingHours && !document.km)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.AcquisitionKm') });

      if (document.isSecondHand && !document.buildYearAndMonth)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.BuildYearAndMonth') });

      if (document.isChassisNumberAvailable && !document.chassisNumber)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.ChassisNumber') });

      if (workLocationIsMandatory) emptyRequiredProps.push({ name: t('OfferEdit.Asset.GeneralDetails.WorkLocation') });

      if (financedAssetDealerWrite && !isFacilityFrame) {
        mandatoryDealerAssetFields.forEach(field => {
          if (!document.partner.dealer || !document.partner.dealer[field.key]) emptyRequiredProps.push({ name: t(field.value) });
        });
      }

      if (financedAssetDealerWrite && document.partner.supplierType === supplierType.Other) {
        mandatorySupplierAssetFields.forEach(field => {
          if (!document.partner.supplier || !document.partner.supplier[field.key])
            emptyRequiredProps.push({
              name: field.key === 'registrationCode' ? `${t(mandatorySupplierAssetFields[0].value)} ${t(field.value)}` : t(field.value)
            });
        });
      }

      return emptyRequiredProps;
    }, [
      t,
      document,
      mandatoryPropertiesConfigured,
      isFacilityFrame,
      financedAssetDealerWrite,
      hasWorkingHours,
      getItemHierarchy,
      workLocationIsMandatory
    ]);

    const requiredFieldsRefs = useRef([]);
    requiredFieldsRefs.current = [];

    const addToRefs = (ref, elemName, elemType) => {
      const isEmptyMandatory = getMandatoryFields.find(el => el.name === elemName);

      if (ref && !requiredFieldsRefs.current.includes(ref) && isEmptyMandatory)
        requiredFieldsRefs.current.push({ ref: ref, type: elemType });
    };

    const focusRef = (ref, type = 'undefined') => {
      switch (type) {
        case TP_NUM:
        case TP_TEXT:
        case TP_DATE:
          ref.firstElementChild?.firstElementChild?.nextElementSibling?.firstElementChild?.focus();
          break;
        case COMBO:
        case SELECT:
        case SEARCH:
        case SEARCH_BIT:
          ref.firstElementChild?.firstElementChild?.firstElementChild?.nextElementSibling?.firstElementChild?.focus();
          break;
        default:
          break;
      }
    };

    const debouncedScrollAndFocus = debounce(fieldRef => {
      fieldRef.ref.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      focusRef(fieldRef.ref, fieldRef.type);
    }, 500);

    const handleClickToScroll = fieldName => {
      const fieldRef = requiredFieldsRefs.current.find(el => el.ref?.id?.toString() === fieldName);

      if (!fieldRef) return;

      if (document?.properties?.find(field => field?.name === fieldName)) {
        setExpandedCards(prevState => {
          return { ...prevState, assetPropertiesCardExpanded: true };
        });
      } else if (fieldName === 'Dealer' || fieldName === 'Supplier' || fieldName === 'Supplier CUI')
        setExpandedCards(prevState => {
          return { ...prevState, dealerCardExpanded: true };
        });
      debouncedScrollAndFocus(fieldRef);
    };

    const handleCardToggle = useCallback(
      cardExpanded => () => {
        setExpandedCards(prevState => {
          return { ...prevState, [cardExpanded]: !prevState[cardExpanded] };
        });
      },
      []
    );

    return (
      <form noValidate>
        <Grid container spacing={3} alignItems='center'>
          <OfferAssetWorkingLocationComponent
            asset={asset}
            onPropertyChange={onPropertyChange}
            processing={processing}
            disabled={rights.readOnlyFull || !financedAssetWrite}
            addToRefs={addToRefs}
          />
          <Grid item xs={12} lg={8}>
            {!isFacilityFrame && (
              <>
                <OfferAssetSecondHandComponent
                  isSecondHand={document.isSecondHand}
                  buildYearAndMonth={document.buildYearAndMonth}
                  buildYearAndMonthError={validationInfo.buildYearAndMonth}
                  secondHandProperty={secondHandProperty}
                  onPropertyChange={onPropertyChange}
                  maxMonths={maxMonths}
                  selfPartnerName={selfPartnerName}
                  processing={processing}
                  disabled={{ isSecondHand: true }}
                  addToRefs={addToRefs}
                />
                <OfferAssetChassisNumberComponent
                  isChassisNumberAvailable={document.isChassisNumberAvailable}
                  chassisNumberDisabled={chassisNumberDisabled}
                  chassisNumberTooltip={chassisNumberTooltip}
                  chassisNumber={localChassisNumber}
                  processing={processing}
                  chassisNumberError={validationInfo.chassisNumber}
                  onPropertyChange={onPropertyChange}
                  onAssetDataChange={onAssetDataChange}
                  addToRefs={addToRefs}
                />
              </>
            )}

            <AssetOrderContainer
              onPropertyChange={onPropertyChange}
              isFacilityFrame={isFacilityFrame}
              validationInfo={validationInfo.category}
              addToRefs={addToRefs}
              onSearch={onAssetDataChange}
            />
            <br />

            <Grid container spacing={3}>
              <AssetSearchComponent
                onPropertyChange={onPropertyChange}
                buildYearAndMonth={getDateValue(document.buildYearAndMonth)}
                showQuantity={true}
                quantity={document.quantity}
                quantityDisabled={quantityDisabled}
                quantityTooltip={quantityTooltip}
                quantityError={validationInfo.quantity}
                withIndividualAssetData={document.withIndividualAssetData}
                processing={processing}
                addToRefs={addToRefs}
              />

              <OfferAssetFinancialComponent
                acquisitionPriceWithoutVat={document.acquisitionPriceWithoutVat}
                reevaluationPriceWithoutVat={document.reevaluationPriceWithoutVat}
                currencyId={document.currencyId}
                acquisitionPriceWithVat={document.acquisitionPriceWithVat}
                acquisitionPriceWithoutVatError={validationInfo.acquisitionPriceWithoutVat}
                acquisitionPriceWithVatError={validationInfo.acquisitionPriceWithVat}
                currencies={currencies}
                processing={processing}
                onPropertyChange={onPropertyChange}
                addToRefs={addToRefs}
              />
            </Grid>

            {document.firstOwner && (
              <OfferAssetFirstOwnerComponent
                onPropertyChange={onPropertyChange}
                firstOwner={document.firstOwner}
                processing={processing}
                selfPartnerName={selfPartnerName}
              />
            )}
          </Grid>
          <Grid item xs={12} lg={4}>
            {assetImage && (
              <Grid container justifyContent='center'>
                <Grid item xs={12} sm={10} md={8} lg={12} p={1} className='fileInput text-center'>
                  <OfferAssetImage src={assetImage} alt='...' />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item container>
            <Grid item xs={12} lg={9} sx={{ order: underLg ? 2 : 1, pr: { xs: 0, lg: 2 } }}>
              <Grid container spacing={2}>
                {financedAssetDealerRead && (
                  <Grid item xs={12}>
                    <CollapseCard
                      id={'offerAssetCard_partnerCard'}
                      title={t('OfferEdit.Asset.Partner.Dealer')}
                      expanded={expandedCards.dealerCardExpanded}
                      onToggle={handleCardToggle('dealerCardExpanded')}
                      toggleOnHeaderClick
                    >
                      <OfferAssetPartnerComponent
                        partner={document.partner}
                        shouldUseUsersAsContactPersonsForDealer={document.shouldUseUsersAsContactPersonsForDealer}
                        onPropertyChange={addPartnerPrefix(onPropertyChange)}
                        loadDealers={loadDealers}
                        loadSuppliers={loadSuppliers}
                        loadPartnerContactInfo={loadPartnerContactInfo}
                        localSupplier={localSupplier}
                        processing={processing}
                        validationInfo={validationInfo.partner}
                        isSearchingRestrictedDealer={isSearchingRestrictedDealer}
                        addToRefs={addToRefs}
                      />
                    </CollapseCard>
                  </Grid>
                )}
                {document.withIndividualAssetData && (
                  <Grid item xs={12}>
                    <CollapseCard title={t('OfferEdit.AssetInformations.Title')} toggleOnHeaderClick>
                      <OfferAssetInformationsContainer
                        leasingDocumentAssetsInformations={document.assetInformations}
                        documentId={document.documentId}
                        siteId={document.siteId}
                        documentDetailId={document.documentDetailId}
                        editRight={editRight}
                      />
                    </CollapseCard>
                  </Grid>
                )}
                {!isFacilityFrame && !!document.itemId && (
                  <Grid item xs={12}>
                    <CollapseCard
                      id={'offerAssetCard_propertiesCard'}
                      title={t('OfferEdit.Asset.AssetProperties')}
                      expanded={expandedCards.assetPropertiesCardExpanded}
                      onToggle={handleCardToggle('assetPropertiesCardExpanded')}
                      toggleOnHeaderClick
                    >
                      <OfferAssetPropertiesComponent
                        properties={document.properties}
                        onPropertyChange={addPropertiesPrefix(onPropertyChange)}
                        validationInfo={validationInfo.properties}
                        loadPropertyDetails={loadPropertyDetails}
                        processing={processing}
                        addToRefs={addToRefs}
                      />
                    </CollapseCard>
                  </Grid>
                )}
                {!isFacilityFrame && enableAssetConfigurator && (
                  <Grid item xs={12}>
                    <CollapseCard title={t('OfferEdit.Asset.Equipments.Title')} toggleOnHeaderClick>
                      <OfferAssetEquipmentsComponent processing={processing} equipments={document.equipments} />
                    </CollapseCard>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <StickyGridWrapper item xs={12} lg={3} sx={{ order: underLg ? 1 : 2 }}>
              <StickyGrid container spacing={2} xs={12}>
                <Grid item xs={12}>
                  <OfferAssetRequiredPropertiesComponent
                    properties={getMandatoryFields}
                    handleClickToScroll={handleClickToScroll}
                    assetPropertiesAreLoaded={assetPropertiesAreLoaded}
                  />
                </Grid>
                <Grid item container xs={12} spacing={2} alignItems='center' justifyContent='space-between'>
                  <Grid item xs>
                    {!rights.isImage && (
                      <Button
                        id={'offerAssetCard_saveBtn'}
                        sx={{ width: 1 }}
                        color='primary'
                        variant='contained'
                        onClick={onSave}
                        disabled={
                          saving ||
                          rights.readOnlyFull ||
                          !rights.financedAssetWrite ||
                          !isDirty ||
                          !validationInfo.isValid ||
                          processing ||
                          !assetPropertiesAreLoaded ||
                          getMandatoryFields.length > 0
                        }
                      >
                        {saving ? t('Saving') : t('Save')}
                      </Button>
                    )}
                  </Grid>
                  <OfferAssetOptionsWrapper item xs='auto'>
                    {assetChassisNumberButtonVisibleRule && !onlyOneActionRuleIsVisible && openOptions && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <OfferAssetChassisNumberPopupContainer
                            onPropertyChange={onPropertyChange}
                            chassisNumberError={validationInfo.chassisNumber}
                            handleChassisNumberSave={handleChassisNumberSave}
                            handleCancelChassisNumberSave={handleCancelChassisNumberSave}
                            isValid={validationInfo.isValid}
                            processing={processing}
                            localChassisNumber={localChassisNumber}
                            chassisNumber={document.chassisNumber}
                            updating={updatingChassisNumber}
                          />
                        </Grid>
                      </Grow>
                    )}

                    {assetChassisNumberButtonVisibleRule &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('OfferAssetChassisNumberPopupContainer') && (
                        <Grid item xs='auto'>
                          <OfferAssetChassisNumberPopupContainer
                            onPropertyChange={onPropertyChange}
                            chassisNumberError={validationInfo.chassisNumber}
                            handleChassisNumberSave={handleChassisNumberSave}
                            handleCancelChassisNumberSave={handleCancelChassisNumberSave}
                            isValid={validationInfo.isValid}
                            processing={processing}
                            localChassisNumber={localChassisNumber}
                            chassisNumber={document.chassisNumber}
                            updating={updatingChassisNumber}
                          />
                        </Grid>
                      )}
                    {configurationButtonVisibleRule && !onlyOneActionRuleIsVisible && openOptions && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <IconButton
                            id={'offerAssetCard_assetActions_equipmentsConfigurator'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('Tooltips.Configurator')}
                            onClick={openConfigurator}
                          >
                            <Build />
                          </IconButton>
                        </Grid>
                      </Grow>
                    )}
                    {propertiesDuplicateVisibleRule && !rights.isImage && asset?.document?.documentDetailId && !duplicating && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <Grid item xs>
                            <IconButton
                              id={'offerAssetCard_duplicateBtn'}
                              color='primary'
                              variant='text'
                              size='small'
                              tooltip={t('Duplicate')}
                              onClick={onDuplicate}
                            >
                              <FileCopy />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grow>
                    )}
                    {configurationButtonVisibleRule &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('offerAssetCard_assetActions_equipmentsConfigurator') && (
                        <Grid item xs='auto'>
                          <IconButton
                            id={'offerAssetCard_assetActions_equipmentsConfigurator'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('Tooltips.Configurator')}
                            onClick={openConfigurator}
                          >
                            <Build />
                          </IconButton>
                        </Grid>
                      )}
                    {propertiesHistoryButtonVisibleRule && !onlyOneActionRuleIsVisible && openOptions && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <IconButton
                            id={'offerAssetCard_assetActions_propertiesHistory'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('OfferEdit.Asset.Properties.History')}
                            onClick={openPropertiesHistory}
                          >
                            <History />
                          </IconButton>
                        </Grid>
                      </Grow>
                    )}
                    {propertiesHistoryButtonVisibleRule &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('offerAssetCard_assetActions_propertiesHistory') && (
                        <Grid item xs='auto'>
                          <IconButton
                            id={'offerAssetCard_assetActions_propertiesHistory'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('OfferEdit.Asset.Properties.History')}
                            onClick={openPropertiesHistory}
                          >
                            <History />
                          </IconButton>
                        </Grid>
                      )}
                    {!document.withIndividualAssetData &&
                      document.documentDetailId &&
                      document.quantity > 1 &&
                      !onlyOneActionRuleIsVisible &&
                      openOptions && (
                        <Grow in={openOptions}>
                          <Grid item xs='auto'>
                            <IconButton
                              id={'offerAssetCard_assetActions_splitAsset'}
                              color='primary'
                              variant='text'
                              size='small'
                              tooltip={t('OfferEdit.AssetInformations.SplitAsset')}
                              onClick={() => {
                                splitAsset(document.quantity);
                              }}
                            >
                              <LibraryAdd />
                            </IconButton>
                          </Grid>
                        </Grow>
                      )}
                    {!document.withIndividualAssetData &&
                      document.documentDetailId &&
                      document.quantity > 1 &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('offerAssetCard_assetActions_splitAsset') && (
                        <Grid item xs='auto'>
                          <IconButton
                            id={'offerAssetCard_assetActions_splitAsset'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('OfferEdit.AssetInformations.SplitAsset')}
                            onClick={() => {
                              splitAsset(document.quantity);
                            }}
                          >
                            <LibraryAdd />
                          </IconButton>
                        </Grid>
                      )}
                    {actionsVisibleRules && !onlyOneActionRuleIsVisible && (
                      <Grid item xs='auto'>
                        {!openOptions ? (
                          <IconButton
                            id={'offerAssetCard_assetActions_showBtn'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('Tooltips.ShowAssetActions')}
                            onClick={handleOpenOptions}
                          >
                            <List />
                          </IconButton>
                        ) : (
                          <IconButton
                            id={'offerAssetCard_assetActions_hideBtn'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('Tooltips.HideAssetActions')}
                            onClick={handleCloseOptions}
                          >
                            <Remove />
                          </IconButton>
                        )}
                      </Grid>
                    )}
                  </OfferAssetOptionsWrapper>
                </Grid>
              </StickyGrid>
            </StickyGridWrapper>
          </Grid>
        </Grid>
      </form>
    );
  }
);

OfferAssetComponentAlpha.displayName = 'OfferAssetComponent';

OfferAssetComponentAlpha.propTypes = {
  t: PropTypes.func.isRequired,
  asset: PropTypes.object,
  hierarchy: PropTypes.object,
  assetImage: PropTypes.string,
  assetTypes: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  duplicating: PropTypes.bool.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired,
  loadDealers: PropTypes.func.isRequired,
  loadSuppliers: PropTypes.func.isRequired,
  loadPartnerContactInfo: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  isSearchingRestrictedDealer: PropTypes.bool,
  handleChassisNumberSave: PropTypes.func.isRequired,
  handleCancelChassisNumberSave: PropTypes.func.isRequired,
  updatingChassisNumber: PropTypes.bool,
  localChassisNumber: PropTypes.string,
  localSupplier: PropTypes.object,
  updateAssetChassisNo: PropTypes.bool.isRequired,
  financedAssetWrite: PropTypes.bool.isRequired,
  handleOpenOptions: PropTypes.func.isRequired,
  handleCloseOptions: PropTypes.func.isRequired,
  openOptions: PropTypes.bool.isRequired,
  openConfigurator: PropTypes.func,
  maxMonths: PropTypes.number,
  enableConfiguratorButton: PropTypes.bool.isRequired,
  selfPartnerName: PropTypes.string.isRequired,
  editRight: PropTypes.bool.isRequired,
  onAssetDataChange: PropTypes.func,
  splitAsset: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  isFacilityFrame: PropTypes.bool,
  openPropertiesHistory: PropTypes.func.isRequired,
  hasWorkingHours: PropTypes.bool,
  assetPropertiesAreLoaded: PropTypes.bool.isRequired
};

export default compose(withTranslation())(withRights(OfferAssetComponentAlpha));
